import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Text from 'tio-ui/components/text';
import {
  PhWarningCircle,
  PhCaretDown,
  PhCaretUp,
  PhXCircle,
  PhCheckCircle,
} from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';

interface AddressOption {
  id: string;
  label: string;
}

interface AddressSelectionCardSignature {
  Args: {
    /** Header title for the issue */
    title: string;
    /** Description shown below the title */
    description: string;
    /** Available addresses for selection */
    addresses: AddressOption[];
    /** Called when user submits an address selection; receives the selected address id */
    onSubmit: (addressId: string) => Promise<void>;
  };
  Element: HTMLDivElement;
}

export default class AddressSelectionCard extends Component<AddressSelectionCardSignature> {
  @service declare intl: IntlService;

  @tracked isExpanded = false;
  @tracked selectedAddressId: string | null = null;
  @tracked isDropdownOpen = false;
  @tracked isSuccess = false;
  @tracked hasError = false;

  get selectedAddress(): AddressOption | undefined {
    return this.args.addresses.find((a) => a.id === this.selectedAddressId);
  }

  get isSubmitDisabled(): boolean {
    return !this.selectedAddressId || this.handleSubmit.isRunning;
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  toggleDropdown = () => {
    this.isDropdownOpen = !this.isDropdownOpen;
  };

  selectAddress = (address: AddressOption) => {
    this.selectedAddressId = address.id;
    this.hasError = false;
    this.isDropdownOpen = false;
  };

  clearSelection = () => {
    this.selectedAddressId = null;
    this.hasError = false;
  };

  handleSubmit = dropTask(async (e: Event) => {
    e.preventDefault();
    if (!this.selectedAddressId) return;

    this.hasError = false;

    try {
      await this.args.onSubmit(this.selectedAddressId);
      this.isSuccess = true;
    } catch {
      this.hasError = true;
    }
  });

  <template>
    <div
      class="tio-bg-surface border tio-border-default tio-stack-start gap-6 px-4 py-6 rounded-2xl"
      data-test-address-selection-card
      ...attributes
    >
      {{#if this.isSuccess}}
        {{! ── Success State (Figma: Blank Payment Address / Success) ── }}
        <div class="tio-row w-full" data-test-address-selection-success>
          <div class="tio-row-center h-10 w-10 rounded-full tio-bg-success-soft shrink-0">
            <PhCheckCircle @size={{20}} class="tio-fg-success" @weight="fill" />
          </div>
          <Text @weight="medium" class="tio-fg-secondary">
            {{t "slr.reimagined.address_selection.success_message"}}
          </Text>
        </div>
      {{else}}
        {{! ── Header: Icon + Title/Description + Caret Toggle ── }}
        <button
          type="button"
          class="tio-row-between w-full cursor-pointer"
          data-test-address-selection-toggle
          aria-expanded="{{this.isExpanded}}"
          {{on "click" this.toggleExpanded}}
        >
          <div class="tio-row">
            <div class="tio-row-center rounded-lg shrink-0 size-12 tio-bg-danger-soft">
              <PhWarningCircle @size={{32}} class="tio-fg-danger" />
            </div>
            <div class="tio-stack-start gap-1 text-left">
              <Text
                @weight="semibold"
                class="tio-fg-secondary capitalize tracking-tight leading-snug"
              >
                {{@title}}
              </Text>
              <Text @weight="medium" class="tio-fg-muted leading-snug">
                {{@description}}
              </Text>
            </div>
          </div>
          <div class="tio-row-center shrink-0 ml-4">
            {{#if this.isExpanded}}
              <PhCaretUp @size={{24}} class="tio-fg-secondary" />
            {{else}}
              <PhCaretDown @size={{24}} class="tio-fg-secondary" />
            {{/if}}
          </div>
        </button>

        {{! ── Expanded Content Area ── }}
        {{#if this.isExpanded}}
          <div
            class="tio-bg-info-soft tio-stack-start p-4 rounded-2xl w-full gap-4"
            data-test-address-selection-expanded
          >
            <Text class="tio-fg-secondary leading-relaxed">
              {{t "slr.reimagined.address_selection.instruction"}}
            </Text>

            {{! ── Dropdown Container ── }}
            <div class="relative w-full max-w-md">
              {{#if this.selectedAddress}}
                {{! ── Selected state: show address with clear button ── }}
                <div
                  class="tio-row-between gap-2 w-full tio-bg-surface border tio-border-default rounded-lg px-3 py-2.5 text-sm tio-fg-secondary"
                  data-test-address-selection-selected
                >
                  <span class="truncate">{{this.selectedAddress.label}}</span>
                  <button
                    type="button"
                    class="shrink-0 tio-fg-disabled hover:tio-fg-secondary cursor-pointer"
                    aria-label={{t "slr.reimagined.address_selection.clear_selection"}}
                    data-test-address-selection-clear
                    {{on "click" this.clearSelection}}
                  >
                    <PhXCircle @size={{18}} />
                  </button>
                </div>
              {{else}}
                {{! ── Unselected state: clickable dropdown trigger ── }}
                <button
                  type="button"
                  class="tio-row-between w-full tio-bg-surface border tio-border-default rounded-lg px-3 py-2.5 text-sm cursor-pointer hover:tio-border-strong"
                  data-test-address-selection-trigger
                  aria-haspopup="listbox"
                  aria-expanded="{{this.isDropdownOpen}}"
                  {{on "click" this.toggleDropdown}}
                >
                  <span class="tio-fg-disabled">
                    {{t "slr.reimagined.address_selection.placeholder"}}
                  </span>
                  {{#if this.isDropdownOpen}}
                    <PhCaretUp @size={{16}} class="tio-fg-muted" />
                  {{else}}
                    <PhCaretDown @size={{16}} class="tio-fg-muted" />
                  {{/if}}
                </button>
              {{/if}}

              {{! ── Dropdown options ── }}
              {{#if this.isDropdownOpen}}
                <ul
                  class="absolute z-10 mt-1 w-full tio-bg-surface border tio-border-default rounded-lg shadow-lg overflow-hidden"
                  role="listbox"
                  data-test-address-selection-options
                >
                  {{#each @addresses as |address|}}
                    <li
                      class="w-full text-left px-3 py-2.5 text-sm tio-fg-secondary hover:tio-bg-sunken cursor-pointer border-b tio-border-subtle last:border-b-0"
                      role="option"
                      aria-selected="{{if (eq this.selectedAddressId address.id) 'true' 'false'}}"
                      data-test-address-option={{address.id}}
                      {{on "click" (fn this.selectAddress address)}}
                    >
                      {{address.label}}
                    </li>
                  {{/each}}
                </ul>
              {{/if}}
            </div>

            {{! ── Error message ── }}
            {{#if this.hasError}}
              <Text @size="sm" class="tio-fg-danger" data-test-address-selection-error>
                {{t "slr.reimagined.address_selection.error_message"}}
              </Text>
            {{/if}}

            {{! ── Submit Button ── }}
            <button
              type="button"
              class="tio-row-center px-6 py-2 rounded-md tio-text-sm-semibold leading-none
                {{if
                  this.isSubmitDisabled
                  'bg-gray-300 tio-fg-inverse cursor-not-allowed'
                  'bg-teal-600 tio-fg-inverse cursor-pointer hover:bg-teal-700'
                }}"
              disabled={{this.isSubmitDisabled}}
              data-test-address-selection-submit
              {{on "click" this.handleSubmit.perform}}
            >
              {{t "slr.reimagined.submit"}}
            </button>
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
